import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MarketplaceHowItWorks } from "@/components/marketplace-how-it-works";
import { ReferralsHowItWorks } from "@/components/referrals-how-it-works";
import { EarnHowItWorks } from "@/components/earn-how-it-works";
import { PromoteHowItWorks } from "@/components/promote-how-it-works";
import { ProfileHowItWorks, PROFILE_FAQ } from "@/components/profile-how-it-works";
import {
  Banknote,
  Coins,
  Flame,
  Gift,
  LayoutGrid,
  LifeBuoy,
  Megaphone,
  PiggyBank,
  Receipt,
  Rocket,
  Sparkles,
  Store,
  Trophy,
  Users,
  Wallet,
} from "lucide-react";
import { ContactForm } from "@/components/contact-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HOW_IT_WORKS_OG_IMAGE, HOW_IT_WORKS_OG_IMAGE_ALT, buildPageMetadata } from "@/lib/seo";
import {
  FAQ,
  GLOSSARY,
  HOW_IT_WORKS_INTRO,
  RANKING_RULES,
  SERVICES,
  STEPS,
  type ServiceIcon,
} from "@/lib/how-it-works-content";
import { SERVICE_META, getHowItWorksContent } from "@/lib/how-it-works-content.i18n";
import { useT } from "@/i18n";
import { MP_HIW_FAQ } from "@/lib/marketplace/how-it-works-content";

const SERVICE_ICONS: Record<ServiceIcon, typeof Flame> = {
  boards: LayoutGrid,
  categories: Trophy,
  listings: Rocket,
  boosts: Flame,
  wallets: Wallet,
  billing: Receipt,
  payouts: Banknote,
  referrals: Gift,
  profiles: Users,
  promote: Megaphone,
  earn: Coins,
  marketplace: Store,
  fund: PiggyBank,
};

export const Route = createFileRoute("/how-it-works")({
  head: () =>
    buildPageMetadata({
      path: "/how-it-works",
      title: "How BeHot.Now Works — Every Service, FAQ & Contact | BeHot.Now",
      description:
        "The complete guide to BeHot.Now: boards and Heat, listings, wallets, referrals, billing, payouts, Promote, Earn, Marketplace and Fund — plus FAQ and a contact form.",
      keywords: [
        "how BeHot.Now works",
        "what is heat",
        "boost a listing",
        "ranking boards explained",
        "pay to rank",
        "live chart rankings",
      ],
      ogTitle: "How BeHot.Now works",
      ogDescription: "Boards, Heat, positions and boost duration explained.",
      image: HOW_IT_WORKS_OG_IMAGE,
      imageAlt: HOW_IT_WORKS_OG_IMAGE_ALT,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "How it works", path: "/how-it-works" },
      ],
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": "https://behot.now/how-it-works#faq",
          mainEntity: FAQ.map(([q, a]) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": "https://behot.now/how-it-works#marketplace-faq",
          name: "BeHot.Now Marketplace FAQ",
          mainEntity: MP_HIW_FAQ.map(([q, a]) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": "https://behot.now/how-it-works#profile-faq",
          name: "BeHot.Now profile and dashboard FAQ",
          mainEntity: PROFILE_FAQ.map(([q, a]) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        },
        {
          "@context": "https://schema.org",
          "@type": "HowTo",
          "@id": "https://behot.now/how-it-works#howto",
          name: "How to rank on a BeHot.Now board",
          description: HOW_IT_WORKS_INTRO,
          step: STEPS.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.title.replace(/^\d+\.\s*/, ""),
            text: s.body,
            url: `https://behot.now/how-it-works#step-${i + 1}`,
          })),
        },
        {
          "@context": "https://schema.org",
          "@type": "DefinedTermSet",
          "@id": "https://behot.now/how-it-works#glossary",
          name: "BeHot.Now glossary",
          hasDefinedTerm: GLOSSARY.map(([term, def]) => ({
            "@type": "DefinedTerm",
            name: term,
            description: def,
          })),
        },
        {
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": "https://behot.now/how-it-works#services",
          name: "BeHot.Now services",
          itemListElement: SERVICES.map((svc, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: svc.title,
            description: svc.body,
            ...(svc.to ? { url: `https://behot.now${svc.to}` } : {}),
          })),
        },
      ],
    }),
  component: HowItWorks,
});

const TOC_IDS = [
  ["steps", "hiw.toc.steps"],
  ["ranking-rules", "hiw.toc.rules"],
  ["entry-example", "hiw.toc.entry"],
  ["glossary", "hiw.toc.glossary"],
  ["pricing", "hiw.toc.pricing"],
  ["faq", "hiw.toc.faq"],
  ["roadmap", "hiw.toc.roadmap"],
  ["services", "hiw.toc.services"],
  ["contact", "hiw.toc.contact"],
] as const;

type SectionTab = "boards" | "marketplace" | "promote" | "referrals" | "earn" | "profile";

type SectionTabDef = { key: SectionTab; labelKey: string; summaryKey: string; icon: typeof Flame };

const SECTION_TABS: SectionTabDef[] = [
  { key: "boards", labelKey: "hiwpage.tab.boards.label", summaryKey: "hiwpage.tab.boards.summary", icon: Flame },
  { key: "marketplace", labelKey: "hiwpage.tab.marketplace.label", summaryKey: "hiwpage.tab.marketplace.summary", icon: Store },
  { key: "promote", labelKey: "hiwpage.tab.promote.label", summaryKey: "hiwpage.tab.promote.summary", icon: Megaphone },
  { key: "referrals", labelKey: "hiwpage.tab.referrals.label", summaryKey: "hiwpage.tab.referrals.summary", icon: Gift },
  { key: "earn", labelKey: "hiwpage.tab.earn.label", summaryKey: "hiwpage.tab.earn.summary", icon: Banknote },
  { key: "profile", labelKey: "hiwpage.tab.profile.label", summaryKey: "hiwpage.tab.profile.summary", icon: Users },
];

/** Boards tab only documents board-side services; the rest live in their own tabs. */
const BOARD_SERVICE_KEYS = new Set([
  "boards",
  "categories",
  "listings",
  "boosts",
  "wallets",
  "billing",
  "profiles",
  "fund",
]);

function HowItWorks() {
  const { t, locale } = useT();
  const content = getHowItWorksContent(locale);
  const [tab, setTab] = useState<SectionTab>("boards");

  return (
    <main className="mx-auto max-w-4xl px-4 py-14">
      <article itemScope itemType="https://schema.org/WebPage">
        <header className="bg-glow relative overflow-hidden rounded-2xl border border-border bg-card shadow-card">
          <div className="bg-gradient-hot h-1.5 w-full" aria-hidden="true" />
          <div className="p-6 sm:p-10">
            <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary uppercase tracking-widest">
              <Sparkles className="mr-1.5 size-3.5" aria-hidden="true" />
              {t("hiwpage.badge")}
            </Badge>
            <h1
              className="mt-5 max-w-2xl font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl"
              itemProp="headline"
            >
              <span className="text-gradient-hot">{t("hiwpage.hero.title")}</span>
            </h1>
            <p
              className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg"
              itemProp="description"
            >
              {t("hiwpage.hero.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-px border-t border-border bg-border sm:grid-cols-4">
              {([
                { label: t("hiwpage.chip.listIt"), Icon: Rocket },
                { label: t("hiwpage.chip.boostIt"), Icon: Flame },
                { label: t("hiwpage.chip.climb"), Icon: Trophy },
                { label: t("hiwpage.chip.getSeen"), Icon: Megaphone },
              ] as const).map(({ label, Icon }) => (
                <span
                  key={label}
                  className="inline-flex min-h-14 items-center justify-center gap-2 bg-card px-3 text-center text-[11px] font-bold uppercase tracking-widest sm:text-xs"
                >
                  <Icon className="size-4 shrink-0 text-primary" aria-hidden="true" />
                  {label}
                </span>
              ))}
          </div>
        </header>

        <div
          className="sticky top-16 z-20 mt-6 grid grid-cols-1 gap-2 rounded-xl border border-border bg-background/95 p-2 shadow-card backdrop-blur sm:static sm:grid-cols-3"
          role="tablist"
          aria-label={t("hiwpage.sections.aria")}
        >
          {SECTION_TABS.map(({ key, labelKey, summaryKey, icon: Icon }) => (
            <Button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              variant={tab === key ? "hot" : "ghost"}
              className="h-auto min-h-16 w-full min-w-0 items-start justify-start gap-3 whitespace-normal px-3 py-3 text-left"
            >
              <Icon className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold leading-tight">{t(labelKey)}</span>
                <span className={`mt-1 block text-xs font-normal leading-snug ${tab === key ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                  {t(summaryKey)}
                </span>
              </span>
            </Button>
          ))}

        </div>

        {tab === "marketplace" && <MarketplaceHowItWorks />}
        {tab === "promote" && <PromoteHowItWorks />}
        {tab === "referrals" && <ReferralsHowItWorks />}
        {tab === "earn" && <EarnHowItWorks />}
        {tab === "profile" && <ProfileHowItWorks />}

        {tab === "boards" && (
          <>
        <nav className="mt-8" aria-label={t("hiw.toc")}>
          <h2 className="sr-only">{t("hiw.toc.title")}</h2>
          <ul className="flex flex-wrap gap-2">
            {TOC_IDS.map(([id, key]) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  className="inline-flex rounded-full border border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                >
                  {t(key)}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <section className="mt-12" id="steps" aria-labelledby="steps-heading">
          <h2 id="steps-heading" className="font-display text-2xl font-bold">
            {t("hiw.steps.title")}
          </h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <ol className="contents list-none">
              {content.steps.map((s, i) => (
                <li key={s.title} id={`step-${i + 1}`} className="card-surface p-5">
                  <h3 className="font-display text-lg font-bold">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
                </li>
              ))}
            </ol>
            <div className="card-surface flex flex-col justify-center p-5">
              <h3 className="font-display text-lg font-bold">{t("hiw.ready.title")}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t("hiw.ready.body")}</p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <Button asChild variant="hot" className="w-full sm:w-auto">
                  <Link to="/get-hot">{t("hiw.ready.list")}</Link>
                </Button>
                <Button asChild variant="outline" className="w-full sm:w-auto">
                  <Link to="/hot">{t("hiw.ready.browse")}</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-14" id="ranking-rules" aria-labelledby="ranking-rules-heading">
          <h2 id="ranking-rules-heading" className="font-display text-2xl font-bold">
            {t("hiw.rules.title")}
          </h2>
          <div className="card-surface mt-5 p-6">
            <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
              {content.rankingRules.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-14" id="entry-example" aria-labelledby="entry-example-heading">
          <h2 id="entry-example-heading" className="font-display text-2xl font-bold">
            {t("hiw.entry.title")}
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">{t("hiw.entry.intro")}</p>
          <div className="card-surface mt-5 overflow-x-auto p-5">
            <div className="grid min-w-[280px] gap-4">
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background/50 px-4 py-3">
                <span className="text-sm text-muted-foreground">{t("hiw.entry.lowest")}</span>
                <span className="font-display text-xl font-bold text-primary">
                  {t("hiw.heat", { n: 12 })}
                </span>
              </div>
              <div className="flex items-center justify-center">
                <span className="rounded-full bg-border px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("hiw.entry.plus")}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background/50 px-4 py-3">
                <span className="text-sm text-muted-foreground">{t("hiw.entry.increment")}</span>
                <span className="font-display text-xl font-bold">{t("hiw.heat", { n: 1 })}</span>
              </div>
              <div className="flex items-center justify-center">
                <span className="text-2xl text-muted-foreground" aria-hidden="true">
                  =
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
                <span className="text-sm font-semibold text-foreground">{t("hiw.entry.min")}</span>
                <span className="font-display text-2xl font-bold text-primary">
                  {t("hiw.heat", { n: 13 })}
                </span>
              </div>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">{t("hiw.entry.note")}</p>
          </div>
        </section>

        <section className="mt-14" id="glossary" aria-labelledby="glossary-heading">
          <h2 id="glossary-heading" className="font-display text-2xl font-bold">
            {t("hiw.glossary.title")}
          </h2>
          <dl className="mt-5 grid gap-3 sm:grid-cols-2">
            {content.glossary.map(([term, def]) => (
              <div key={term} className="card-surface p-4">
                <dt className="flex items-center gap-1.5 font-display font-bold">
                  <Flame className="size-4 text-primary" aria-hidden="true" /> {term}
                </dt>
                <dd className="mt-1 text-sm text-muted-foreground">{def}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="mt-14" id="pricing" aria-labelledby="pricing-heading">
          <h2 id="pricing-heading" className="font-display text-2xl font-bold">
            {t("hiw.pricing.title")}
          </h2>
          <div className="card-surface mt-5 grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {t("hiw.pricing.oneHeat")}
              </p>
              <p className="mt-1 font-display text-2xl font-bold text-primary">US$1.00</p>
              <p className="text-xs text-muted-foreground">{t("hiw.pricing.oneHeatNote")}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {t("hiw.pricing.listing")}
              </p>
              <p className="mt-1 font-display text-2xl font-bold">{t("hiw.heat", { n: 3 })}</p>
              <p className="text-xs text-muted-foreground">{t("hiw.pricing.listingNote")}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {t("hiw.pricing.entry")}
              </p>
              <p className="mt-1 font-display text-2xl font-bold">{t("hiw.pricing.entryValue")}</p>
              <p className="text-xs text-muted-foreground">{t("hiw.pricing.entryNote")}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {t("hiw.pricing.opening")}
              </p>
              <p className="mt-1 font-display text-2xl font-bold">{t("hiw.heat", { n: 5 })}</p>
              <p className="text-xs text-muted-foreground">{t("hiw.pricing.openingNote")}</p>
            </div>
          </div>
        </section>

        <section className="mt-14" id="faq" aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="font-display text-2xl font-bold">
            {t("hiw.faq.title")}
          </h2>
          <div className="mt-5 divide-y divide-border rounded-xl border border-border">
            {content.faq.map(([q, a], i) => (
              <details
                key={q}
                id={`faq-${i + 1}`}
                open={i < 3}
                className="group px-5 py-4 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="cursor-pointer list-none font-display font-bold">{q}</summary>
                <p className="mt-2 text-sm text-muted-foreground">{a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-14" id="roadmap" aria-labelledby="roadmap-heading">
          <h2 id="roadmap-heading" className="font-display text-2xl font-bold">
            {t("hiw.roadmap.title")}
          </h2>
          <ul className="mt-5 grid list-none gap-4 sm:grid-cols-2">
            <li className="card-surface p-5">
              <Sparkles className="size-5 text-creative" aria-hidden="true" />
              <h3 className="mt-3 font-display text-lg font-bold">{t("hiwpage.roadmap.promote")}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{t("hiw.promote.body")}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => setTab("promote")}>
                  {t("hiwpage.roadmap.promoteGuide")}
                </Button>
                <Button asChild variant="ghost" size="sm">
                  <Link to="/promote">{t("hiw.promote.more")}</Link>
                </Button>
              </div>
            </li>
            <li className="card-surface p-5">
              <Coins className="size-5 text-success" aria-hidden="true" />
              <h3 className="mt-3 font-display text-lg font-bold">{t("hiwpage.roadmap.earn")}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{t("hiw.earn.body")}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => setTab("earn")}>
                  {t("hiwpage.roadmap.earnGuide")}
                </Button>
                <Button asChild variant="ghost" size="sm">
                  <Link to="/earn">{t("hiw.earn.more")}</Link>
                </Button>
              </div>
            </li>
          </ul>
        </section>

        <section className="mt-14" id="services" aria-labelledby="services-heading">
          <h2 id="services-heading" className="font-display text-2xl font-bold">
            {t("hiw.services.title")}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{t("hiw.services.intro")}</p>
          <ul className="mt-5 grid list-none gap-4 sm:grid-cols-2">
            {content.services
              .filter((s) => BOARD_SERVICE_KEYS.has(s.key))
              .map((s) => {
              const Icon = SERVICE_ICONS[s.key];
              const meta = SERVICE_META[s.key];
              return (
                <li key={s.key} id={`service-${s.key}`} className="card-surface p-5">
                  <div className="flex items-center gap-2">
                    <Icon className="size-5 text-primary" aria-hidden="true" />
                    <h3 className="font-display text-lg font-bold">{s.title}</h3>
                    {meta?.soon && <Badge variant="outline">{t("hiw.soon")}</Badge>}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
                  {meta?.to && (
                    <div className="mt-4">
                      <Button asChild variant="outline" size="sm">
                        <Link to={meta.to}>{t("hiw.services.open", { name: s.title })}</Link>
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        <section className="mt-14" id="contact" aria-labelledby="contact-heading">
          <div className="flex items-center gap-2">
            <LifeBuoy className="size-5 text-primary" aria-hidden="true" />
            <h2 id="contact-heading" className="font-display text-2xl font-bold">
              {t("hiw.contact.title")}
            </h2>
          </div>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{t("hiw.contact.intro")}</p>
          <div className="mt-5">
            <ContactForm />
          </div>
        </section>

        <section className="mt-14 text-center" aria-labelledby="cta-heading">
          <Trophy className="mx-auto size-6 text-primary" aria-hidden="true" />
          <h2 id="cta-heading" className="mt-3 font-display text-2xl font-bold">
            {t("hiw.cta.title")}
          </h2>
          <Button asChild variant="hot" size="lg" className="mt-4">
            <Link to="/get-hot" search={{ listing: undefined, category: undefined }}>
              {t("hiw.cta.button")}
            </Link>
          </Button>
        </section>
          </>
        )}
      </article>
    </main>
  );
}
